import classNames from 'classnames';
import { useMemo } from 'react';

import ProductSpecification from '../../../../b2c/shared/components/ProductSpecification/ProductSpecification';
/*
 * A file in the app directory shouldn't have dependencies from the b2c directory.
 * TODO: Move the component and the ProductFamilyPage-related code to the b2c directory
 * https://dev.azure.com/fortedigital/Flokk/_workitems/edit/14056
 */
import { TabbedInterfaceItem } from '../../../../b2c/shared/components/TabbedInterface/TabbedInterface';
import Tabs from '../../../../b2c/shared/components/Tabs/Tabs';
import useComponentInstanceCreator from '../../../../b2c/shared/hooks/useComponentInstanceCreator';
import { PropertyData } from '../../../data/model';
import { ProductModelDetailsSectionTranslation } from '../../../localization/SiteTranslation';
import { AnalyticsAttributes } from '../../../shared/analytics/AnalyticsAttributes';
import AssetsDownloader from '../../../shared/components/AssetsDownloader/AssetsDownloader';
import { AssetsDownloaderState } from '../../../shared/components/AssetsDownloader/AssetsDownloaderState';
import ProductEnvironmentalData, {
  ProductModelEnvironmentalData,
} from './ProductEnvironmentalData/ProductEnvironmentalData';
import css from './ProductModelDetails.css';

const ANALYTICS_PRODUCT_DETAILS_TAB_ID = 'Product details tab';

export interface ProductModelDetailsData {
  configurationCode: string;
  name: string;
  specification: PropertyData[];
  specificationDescription: string;
  environmentalData: ProductModelEnvironmentalData;
}

interface ProductModelDetailsProps {
  assetsDownloaderState: AssetsDownloaderState;
  model: ProductModelDetailsData;
  translation: ProductModelDetailsSectionTranslation;
  className?: string;
}

const ID = 'product-model-details';

const ProductModelDetails = ({ assetsDownloaderState, className, model, translation }: ProductModelDetailsProps) => {
  /*
   * The AssetsDownloader component is recreated when the assetsDownloaderInstanceKey changes in order to close all expanded Accordion components inside it.
   * An accordion component uses a fully uncontrolled ExpandablePanel component so it's not possible to change its internal state outside a component.
   * */
  const [assetsDownloaderInstanceKey, resetAssetsDownloaderInstanceKey] = useComponentInstanceCreator();

  const tabs = useMemo<TabbedInterfaceItem[]>(() => {
    const { configurationCode, name, environmentalData, specification, specificationDescription } = model;
    const result: TabbedInterfaceItem[] = [];

    // TODO: Environmental data section is currently hidden due to outdated data stored in Virto
    const doesEnvironmentalDataExist = false;
    // environmentalData.co2EmissionKg ||
    // environmentalData.energyUsageMj ||
    // environmentalData.recyclableMaterialsPercentage ||
    // environmentalData.recycledAluminiumPercentage ||
    // environmentalData.recycledMaterialsTotalPercentage ||
    // environmentalData.recycledPlasticPercentage;

    if (doesEnvironmentalDataExist) {
      result.push({
        name: translation.environmentalDataSectionTitle,
        content: (
          <ProductEnvironmentalData
            certificates={environmentalData.certificates}
            co2EmissionKg={environmentalData.co2EmissionKg}
            energyUsageMj={environmentalData.energyUsageMj}
            recyclableMaterialsPercentage={environmentalData.recyclableMaterialsPercentage}
            recycledAluminiumPercentage={environmentalData.recycledAluminiumPercentage}
            recycledMaterialsTotalPercentage={environmentalData.recycledMaterialsTotalPercentage}
            recycledPlasticPercentage={environmentalData.recycledPlasticPercentage}
            translation={translation.environmentalDataSection}
          />
        ),
        innerClassName: css.fullWidthTab,
        ...AnalyticsAttributes.get({
          id: ANALYTICS_PRODUCT_DETAILS_TAB_ID,
          label: translation.environmentalDataSectionTitle,
          product: name,
          'product-code': configurationCode,
        }),
      });
    }

    if (specificationDescription || specification.any()) {
      result.push({
        name: translation.specificationSectionTitle,
        content: <ProductSpecification description={specificationDescription} properties={specification} />,
        ...AnalyticsAttributes.get({
          id: ANALYTICS_PRODUCT_DETAILS_TAB_ID,
          label: translation.specificationSectionTitle,
          product: name,
          'product-code': configurationCode,
        }),
      });
    }

    if (assetsDownloaderState.categories.any()) {
      result.push({
        name: translation.downloadsSectionTitle,
        content: (
          <AssetsDownloader
            state={assetsDownloaderState}
            translation={translation.assetsDownloader}
            key={assetsDownloaderInstanceKey}
            {...AnalyticsAttributes.get({
              product: name,
              'product-code': configurationCode,
            })}
          />
        ),
        ...AnalyticsAttributes.get({
          id: ANALYTICS_PRODUCT_DETAILS_TAB_ID,
          label: translation.downloadsSectionTitle,
          product: name,
          'product-code': configurationCode,
        }),
        onClose: resetAssetsDownloaderInstanceKey,
      });
    }

    return result;
  }, [assetsDownloaderInstanceKey, assetsDownloaderState, model, resetAssetsDownloaderInstanceKey, translation]);

  return (
    <div className={classNames(css.container, className)}>
      <Tabs id={ID} tabs={tabs} />
    </div>
  );
};

export default ProductModelDetails;
